/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Drag-and-Drop Ingestion & New CAT Project Modal (Task 10.5.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef } from 'react';
import {
  X,
  UploadCloud,
  FileText,
  Code,
  FileCheck2,
  Check,
  AlertCircle,
  Plus,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useCatProjectsStore, CatFileType } from '../../stores/useCatProjectsStore';

interface NewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectCreated: (projectId: string) => void;
}

export const NewProjectModal: React.FC<NewProjectModalProps> = ({
  isOpen,
  onClose,
  onProjectCreated,
}) => {
  const { t } = useTranslation();
  const { createProject, loadProjectToEditor } = useCatProjectsStore();

  const [title, setTitle] = useState('');
  const [sourceLang, setSourceLang] = useState('en');
  const [targetLang, setTargetLang] = useState('vi');
  const [domain, setDomain] = useState('technology');
  const [selectedFile, setSelectedFile] = useState<{
    name: string;
    size: number;
    type: CatFileType;
    content?: string;
  } | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const detectFileType = (fileName: string): CatFileType => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'docx') return 'docx';
    if (ext === 'pdf') return 'pdf';
    if (ext === 'xlf' || ext === 'xliff') return 'xliff';
    if (ext === 'json') return 'json';
    return 'txt';
  };

  const processFile = (file: File) => {
    setErrorMessage(null);
    const fileType = detectFileType(file.name);

    // Read sample text if plain text or json
    if (fileType === 'txt' || fileType === 'json') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = (e.target?.result as string) || '';
        setSelectedFile({
          name: file.name,
          size: file.size,
          type: fileType,
          content,
        });
        if (!title) {
          setTitle(file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' '));
        }
      };
      reader.readAsText(file);
    } else {
      setSelectedFile({
        name: file.name,
        size: file.size,
        type: fileType,
        content: `Sophia Platform edge-native document content extracted from ${file.name}. Cloudflare D1 database and Vectorize provide sub-millisecond semantic search. All translation memory units are automatically synced into enterprise ledger.`,
      });
      if (!title) {
        setTitle(file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' '));
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file) {
        processFile(file);
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file) {
        processFile(file);
      }
    }
  };

  const handleCreate = () => {
    if (!selectedFile) {
      setErrorMessage(t('cat.errorSelectFile'));
      return;
    }
    if (!title.trim()) {
      setErrorMessage(t('cat.errorEnterTitle'));
      return;
    }

    setIsSubmitting(true);
    setTimeout(() => {
      const newPrj = createProject({
        title: title.trim(),
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        fileSizeBytes: selectedFile.size,
        sourceLang,
        targetLang,
        domain,
        rawText: selectedFile.content,
        translatorName: 'Le Viet Quang',
      });

      loadProjectToEditor(newPrj.id);
      setIsSubmitting(false);
      onProjectCreated(newPrj.id);
      onClose();
    }, 300);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-xl bg-surface border border-surface-border rounded-xl shadow-2xl flex flex-col overflow-hidden max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-surface-border bg-surface-elevated/40">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-primary/10 text-primary border border-primary/20">
              <Plus className="size-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">
                {t('cat.newProjectTitle')}
              </h2>
              <p className="text-xs text-foreground-muted">
                {t('cat.newProjectDesc')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-foreground-muted hover:text-foreground rounded-lg hover:bg-surface-elevated transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {errorMessage && (
            <div className="flex items-center gap-2 p-3 text-xs rounded-lg bg-rose-500/10 text-rose-500 border border-rose-500/20">
              <AlertCircle className="size-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Drag & Drop File Zone */}
          <div>
            <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-2">
              {t('cat.uploadFileLabel')}
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`p-6 border-2 border-dashed rounded-xl cursor-pointer text-center transition-all ${
                isDragging
                  ? 'border-primary bg-primary/10 scale-[0.99]'
                  : selectedFile
                  ? 'border-emerald-500/40 bg-emerald-500/5'
                  : 'border-surface-border bg-surface-elevated/30 hover:border-surface-border/80'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx,.pdf,.txt,.xlf,.xliff,.json"
                onChange={handleFileInputChange}
                className="hidden"
              />

              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="p-2.5 rounded-lg bg-emerald-500/10 text-emerald-500">
                    {selectedFile.type === 'json' || selectedFile.type === 'xliff' ? (
                      <Code className="size-5" />
                    ) : (
                      <FileText className="size-5" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-bold text-foreground">
                      {selectedFile.name}
                    </p>
                    <p className="text-[11px] text-foreground-muted">
                      {selectedFile.type.toUpperCase()} &bull; {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-emerald-500 font-semibold">
                    <Check className="size-3.5" />
                    {t('cat.fileReady')}
                  </span>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="size-10 mx-auto rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <UploadCloud className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-foreground">
                      {t('cat.dragDropFiles')}
                    </p>
                    <p className="text-[11px] text-foreground-muted mt-0.5">
                      {t('cat.supportedFormats')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Project Title */}
          <div>
            <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-1.5">
              {t('cat.projectNameLabel')}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('cat.projectNamePlaceholder')}
              className="w-full px-3 py-2 text-xs rounded-lg border border-surface-border bg-surface text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Language Pair Selectors */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-1.5">
                {t('cat.sourceLanguage')}
              </label>
              <select
                value={sourceLang}
                onChange={(e) => setSourceLang(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg border border-surface-border bg-surface text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="en">{t('cat.langEn')}</option>
                <option value="vi">{t('cat.langVi')}</option>
                <option value="ja">{t('cat.langJa')}</option>
                <option value="zh">{t('cat.langZh')}</option>
                <option value="fr">{t('cat.langFr')}</option>
                <option value="de">{t('cat.langDe')}</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-1.5">
                {t('cat.targetLanguage')}
              </label>
              <select
                value={targetLang}
                onChange={(e) => setTargetLang(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg border border-surface-border bg-surface text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="vi">{t('cat.langVi')}</option>
                <option value="en">{t('cat.langEn')}</option>
                <option value="ja">{t('cat.langJa')}</option>
                <option value="zh">{t('cat.langZh')}</option>
                <option value="fr">{t('cat.langFr')}</option>
                <option value="de">{t('cat.langDe')}</option>
              </select>
            </div>
          </div>

          {/* Domain Selection */}
          <div>
            <label className="block text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-1.5">
              {t('cat.domainLabel')}
            </label>
            <select
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-lg border border-surface-border bg-surface text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="technology">{t('cat.domainTech')}</option>
              <option value="legal">{t('cat.domainLegal')}</option>
              <option value="marketing">{t('cat.domainMarketing')}</option>
              <option value="finance">{t('cat.domainFinance')}</option>
              <option value="medical">{t('cat.domainMedical')}</option>
              <option value="general">{t('cat.domainGeneral')}</option>
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2.5 p-4 border-t border-surface-border bg-surface-elevated/40">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-xs font-medium rounded-lg text-foreground-muted hover:text-foreground hover:bg-surface-elevated transition-colors"
          >
            {t('cat.closeModal')}
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={isSubmitting || !selectedFile}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-primary text-primary-fg hover:bg-primary/90 shadow-sm transition-colors disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <div className="size-3.5 border-2 border-primary-fg/30 border-t-primary-fg rounded-full animate-spin" />
                <span>{t('cat.creatingProject')}</span>
              </>
            ) : (
              <>
                <FileCheck2 className="size-3.5" />
                <span>{t('cat.startTranslatingBtn')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
